import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Image as ImageIcon, Upload } from 'lucide-react';
import { api, uploadFile } from '@/api/client';
import { SignedImage } from '@/components/ui/SignedImage';

interface FormData {
  name: string;
  code: string;
  brand?: string;
  serviceType: 'AIR' | 'PRIME' | 'SURFACE' | 'EXPRESS' | 'REVERSE';
  orderType: 'B2C' | 'B2B';
  isAggregator: boolean;
  volumetricDivisor: number;
  tatDays?: number;
  maxLiability?: number;
  addressLabelName?: string;
  logoUrl?: string;
  weightCategory?: '0.25KG' | '0.5KG' | '1KG' | '2KG' | '3KG' | '5KG' | '10KG' | '20KG' | '25KG' | '';
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  // Per-courier API service identifier passed in the courier's create-shipment payload
  // (e.g. DTDC: 'Ground Express', 'Priority', 'B2C Smart Express', 'Air Cargo', 'STD EXP-A', 'Premium', 'B2C Priority', 'GEC', 'B2C SMART EXPRESS').
  // Stored in courier.config.serviceTypeId.
  apiServiceTypeId?: string;
  // DTDC load_type — 'DOCUMENT' for document products (Z Series 'STD EXP-A'), otherwise NON-DOCUMENT.
  // Stored in courier.config.loadType.
  apiLoadType?: 'DOCUMENT' | 'NON-DOCUMENT' | '';
  // Docket series this courier is supposed to issue (DTDC 'V', 'X', 'Z', 'D', …).
  // Stored in courier.config.awbSeries; checked against the number the courier actually returns.
  apiAwbSeries?: string;
}

const WEIGHT_CATEGORIES = ['0.25KG','0.5KG','1KG','2KG','3KG','5KG','10KG','20KG','25KG'] as const;

interface Props {
  onSuccess: (created?: any) => void;
  courierId?: number;
  defaultValues?: Partial<FormData>;
}

export function CreateCourierForm({ onSuccess, courierId, defaultValues }: Props) {
  const qc = useQueryClient();
  const isEdit = !!courierId;
  // The logo is stored as an R2 ref, uploaded the moment a file is picked, so the form only ever
  // carries the ref string — never a base64 blob.
  const [logoUrl, setLogoUrl] = useState<string>((defaultValues as any)?.logoUrl ?? '');
  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      orderType: 'B2C',
      serviceType: 'SURFACE',
      volumetricDivisor: 5000,
      isAggregator: false,
      ...defaultValues,
    },
  });

  const save = useMutation({
    mutationFn: async (data: FormData) => {
      const payload: any = {
        ...data,
        code: data.code.toLowerCase().replace(/[^a-z0-9_-]/g, '_'),
        // '' clears a previously set logo; undefined would leave it untouched.
        logoUrl: logoUrl || null,
      };
      if (!payload.weightCategory) delete payload.weightCategory;
      if (!payload.addressLabelName) delete payload.addressLabelName;
      // Pack the courier API identifiers into config.serviceTypeId / config.loadType
      const apiSvc = (payload.apiServiceTypeId ?? '').trim();
      const apiLoad = (payload.apiLoadType ?? '').trim();
      const apiSeries = (payload.apiAwbSeries ?? '').trim().toUpperCase();
      delete payload.apiServiceTypeId;
      delete payload.apiLoadType;
      delete payload.apiAwbSeries;
      if (apiSvc || apiLoad || apiSeries) {
        payload.config = {
          ...(payload.config ?? {}),
          ...(apiSvc ? { serviceTypeId: apiSvc } : {}),
          ...(apiLoad ? { loadType: apiLoad } : {}),
          ...(apiSeries ? { awbSeries: apiSeries } : {}),
        };
      }
      // Edit shouldn't send empty contact* clearing intent — keep simple: strip undefined.
      Object.keys(payload).forEach(k => { if (payload[k] === undefined) delete payload[k]; });
      if (isEdit) {
        // PATCH cannot change `code` once created — drop it to avoid uniqueness conflict
        delete payload.code;
        const r = await api.patch(`/couriers/${courierId}`, payload);
        return r.data;
      }
      const r = await api.post('/couriers', payload);
      return r.data;
    },
    onSuccess: (saved: any) => {
      qc.invalidateQueries({ queryKey: ['couriers'] });
      toast.success(isEdit ? 'Courier updated' : 'Courier created');
      onSuccess(saved);
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? `Failed to ${isEdit ? 'update' : 'create'} courier`);
    },
  });

  return (
    <form onSubmit={handleSubmit((d) => save.mutate(d))} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Courier Name *" error={errors.name?.message}>
          <input {...register('name', { required: 'Required', minLength: { value: 2, message: 'Min 2 chars' } })}
            placeholder="Delhivery" className="input" />
        </Field>
        <Field label="Code *" error={errors.code?.message}>
          <input {...register('code', { required: 'Required', pattern: { value: /^[a-zA-Z0-9_-]+$/, message: 'Letters, numbers, _ or - only' } })}
            disabled={isEdit} placeholder="delhivery_surface" className="input disabled:bg-slate-50 disabled:text-slate-500" />
        </Field>
      </div>

      <Field label="Brand">
        <input {...register('brand')} placeholder="Delhivery Logistics" className="input" />
      </Field>

      <CourierLogoField value={logoUrl} onChange={setLogoUrl} />

      <div className="grid grid-cols-2 gap-3">
        <Field label="Order Type *">
          <select {...register('orderType', { required: true })} className="input">
            <option value="B2C">B2C</option>
            <option value="B2B">B2B</option>
          </select>
        </Field>
        <Field label="Service Type *">
          <select {...register('serviceType', { required: true })} className="input">
            <option value="SURFACE">Surface</option>
            <option value="AIR">Air</option>
            <option value="EXPRESS">Express</option>
            <option value="PRIME">Prime</option>
            <option value="REVERSE">Reverse</option>
          </select>
        </Field>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Field label="Volumetric Divisor">
          <input type="number" {...register('volumetricDivisor', { valueAsNumber: true, min: 1 })} className="input" />
        </Field>
        <Field label="TAT (days)">
          <input type="number" {...register('tatDays', { valueAsNumber: true, min: 1 })} placeholder="3" className="input" />
        </Field>
        <Field label="Max Liability">
          <input type="number" step="0.01" {...register('maxLiability', { valueAsNumber: true })} placeholder="25000" className="input" />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Address Label Name">
          <input {...register('addressLabelName')} placeholder="Delhivery Surface (printed on shipping label)" className="input" />
        </Field>
        <Field label="Weight Category">
          <select {...register('weightCategory')} className="input">
            <option value="">— Select weight slab —</option>
            {WEIGHT_CATEGORIES.map((w) => (
              <option key={w} value={w}>{w.replace('KG', ' kg')}</option>
            ))}
          </select>
        </Field>
      </div>

      {/* API service identifiers. Previously nested inside the "is an aggregator" <label>, so clicking
          the helper text silently toggled that checkbox. */}
      <div className="grid grid-cols-1 gap-3">
        <Field label="Courier API Service Type" error={undefined}>
          <input {...register('apiServiceTypeId')} placeholder="DTDC: Ground Express, Priority, B2C SMART EXPRESS, Air Cargo, STD EXP-A... | Delhivery: leave blank" className="input" />
          <p className="text-[11px] text-slate-500 mt-1">This value is sent to the courier's create-shipment API as <code>service_type_id</code>. Each DTDC sub-product needs its own value.</p>
        </Field>
        <Field label="Load Type (DTDC)" error={undefined}>
          <select {...register('apiLoadType')} className="input">
            <option value="">Default — NON-DOCUMENT (parcel)</option>
            <option value="DOCUMENT">DOCUMENT — document services (e.g. Z Series STD EXP-A)</option>
            <option value="NON-DOCUMENT">NON-DOCUMENT (parcel)</option>
          </select>
          <p className="text-[11px] text-slate-500 mt-1">Sent as <code>load_type</code>. DTDC document products reject a NON-DOCUMENT booking, so pick DOCUMENT for those.</p>
        </Field>
        <Field label="Expected Docket Series" error={undefined}>
          <input {...register('apiAwbSeries')} maxLength={20} placeholder="DTDC: V, X, Z, D, 7X … (leave blank if not applicable)" className="input uppercase" />
          <p className="text-[11px] text-slate-500 mt-1">
            Not sent to the courier — checked against the docket they return. DTDC runs several series off one
            account and picks between them from the service type above, so a wrong service books happily and
            just issues the wrong series. Set this and any mismatch is flagged on the order instead of going unnoticed.
            Comma-separate when a service has more than one valid series (DTDC <code>STD EXP-A</code> is "Z and a few
            regional series" → <code>Z,K</code>; COD on any B2C service returns the <code>I</code> series → <code>7X,I</code>).
          </p>
        </Field>
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" {...register('isAggregator')} className="rounded" />
        This is an aggregator (e.g., Nimbus, Shiprocket)
      </label>

      <div className="border-t border-slate-100 pt-4">
        <div className="text-sm font-medium text-slate-700 mb-2">Contact (optional, for courier reports)</div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Contact Name">
            <input {...register('contactName')} className="input" />
          </Field>
          <Field label="Contact Phone">
            <input {...register('contactPhone')} className="input" />
          </Field>
        </div>
        <Field label="Contact Email">
          <input type="email" {...register('contactEmail')} className="input" />
        </Field>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={() => onSuccess()} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
        <button type="submit" disabled={save.isPending}
          className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-700 transition disabled:opacity-60 flex items-center gap-2">
          {save.isPending && <Loader2 size={16} className="animate-spin" />}
          {isEdit ? 'Save Changes' : 'Create Courier'}
        </button>
      </div>
    </form>
  );
}

function Field({ label, children, error }: { label: string; children: React.ReactNode; error?: string }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
      {error && <p className="text-danger text-xs mt-1">{error}</p>}
    </div>
  );
}

/**
 * Courier logo — printed on the POD for shipments carried by this courier.
 *
 * `Courier.logoUrl` has existed on the model since the beginning but nothing ever set it, so every
 * POD fell back to printing the carrier's name in a box. Uploaded to R2 like the merchant logo, and
 * stored as a ref rather than a data URI so the courier row stays small.
 */
function CourierLogoField({ value, onChange }: { value?: string; onChange: (ref: string) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File) => {
    setError(null);
    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)) {
      setError('Only PNG, JPG or WEBP.'); return;
    }
    if (file.size > 500 * 1024) { setError(`Too large (${(file.size / 1024).toFixed(0)} KB). Max 500 KB.`); return; }
    setBusy(true);
    try {
      const { ref } = await uploadFile(file, 'courier_logo');
      onChange(ref);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? 'Upload failed');
    } finally { setBusy(false); }
  };

  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">
        Courier Logo <span className="text-slate-400 font-normal">· shown on the POD · PNG/JPG, max 500 KB</span>
      </label>
      <div className="flex items-start gap-3">
        <div className="w-24 h-16 rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
          {value
            ? <SignedImage src={value} alt="Courier logo" className="w-full h-full object-contain"
                fallback={<ImageIcon size={20} className="text-slate-300" />} />
            : <ImageIcon size={20} className="text-slate-300" />}
        </div>
        <div className="flex-1 space-y-1.5">
          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/webp" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
              className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-60 flex items-center gap-2">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {value ? 'Replace' : 'Upload'}
            </button>
            {value && (
              <button type="button" onClick={() => onChange('')}
                className="px-3 py-1.5 text-sm text-rose-600 hover:bg-rose-50 rounded-lg">Remove</button>
            )}
          </div>
          {error && <p className="text-danger text-xs">{error}</p>}
          <p className="text-[11px] text-slate-400">
            Use the carrier&apos;s official logo from their partner brand kit. Left blank, the POD prints the
            carrier&apos;s name instead.
          </p>
        </div>
      </div>
    </div>
  );
}
